import { confirm } from '@ardoq/modal';
import { APP_VERSION } from '../appConfig';
import { orgAdminsListCommands } from '../admin/orgAdminsList/commands';
import { getCurrentUserPermissionContext } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { getFeatureContext } from 'streams/featureContext$';
import { hasAccessToSupportChat } from './supportUtils';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';

interface GetSupportContextOpts {
  message?: string;
  chat?: boolean;
  prePopulateMessage?: string | undefined;
}

const getSupportContext = ({
  message = '',
  chat = false,
  prePopulateMessage = undefined,
}: GetSupportContextOpts) => ({
  location: `${window.location.pathname}, hash:${window.location.hash}`,
  versionFront: APP_VERSION,
  versionAPI: window.API_VERSION,
  message,
  chat,
  prePopulateMessage,
});

export const showSupport = async (opts: GetSupportContextOpts = {}) => {
  const supportContext = getSupportContext(opts);

  const permissionContext = getCurrentUserPermissionContext();
  const featureContext = getFeatureContext();
  const hasAccessToChat = hasAccessToSupportChat(
    permissionContext,
    featureContext
  );
  // i18nLocale$.state OK here: snapshot-at-open-time; the dialog/modal renders body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;

  if (!hasAccessToChat) {
    orgAdminsListCommands.openDialog({
      header: t(
        i18nLocale,
        'common:support.chatRestricted.title',
        'In-app chat restricted'
      ),
      infoMessage: t(
        i18nLocale,
        'common:support.chatRestricted.body',
        'Access to in-app chat has been restricted. Please contact your admin if you believe this is a mistake.'
      ),
    });
  } else if (typeof window.Intercom === 'undefined') {
    if (
      await confirm({
        title: t(
          i18nLocale,
          'common:support.unableToLoad.title',
          'Unable to load support'
        ),
        text: (
          <>
            <p>
              {t(
                i18nLocale,
                'common:support.unableToLoad.adblockerHint',
                'Unable to load the support chat. Please try disabling your adblocker or contact us'
              )}{' '}
              <a
                href="mailto:support@ardoq.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(
                  i18nLocale,
                  'common:support.unableToLoad.byEmail',
                  'by email'
                )}
              </a>
              .
            </p>
            <p>
              <em>
                {t(
                  i18nLocale,
                  'common:support.unableToLoad.firewallHint',
                  'This might be due to firewall restrictions if you are on an on-premise installation.'
                )}
              </em>
            </p>
          </>
        ),
        cancelButtonTitle: '', // this hides the cancel button
        confirmButtonTitle: t(
          i18nLocale,
          'common:support.unableToLoad.emailButton',
          'Email Support'
        ),
      })
    ) {
      window.open('mailto:support@ardoq.com', 'email-support');
    }
  } else if (supportContext.prePopulateMessage) {
    window.Intercom('showNewMessage', supportContext.prePopulateMessage);
  } else {
    window.Intercom(supportContext.chat ? 'show' : 'showNewMessage');
  }
};

export const openTrialUpgradeChat = () =>
  showSupport({ prePopulateMessage: "Hi, I'd like to upgrade my trial." });
